import { useEffect, useState } from 'react'
import { type_GM_STATE } from '../../helpers'
import { emitterProps, globalEmitter } from '../helpers/emitter'
import { openModal } from '../helpers/useModal'
import { HeartIcon } from '../icons/HeartIcon'
import { OmneoList, OmneoListItem, type_WISHLIST_DASHBOARD_STATE, typeProduct } from '../types'
import { LoaderSpin } from './LoaderSpin'

type props = {
  product: typeProduct
  productBiLine: string
  variantSelected: any
  variantSelectedId: number
  templateSuffix: string
  className?: string
  disableWishlist: string
  errorMessage: string
  propertiesLocal?: any[]
  isShowOnlyIcon: boolean
  productPersonalisedPrice?: string
}

declare var GM_STATE: type_GM_STATE
declare var wishlistDashboardState: type_WISHLIST_DASHBOARD_STATE

export function WishlistButton({
  product,
  productBiLine,
  variantSelected,
  variantSelectedId,
  templateSuffix,
  className = '',
  disableWishlist,
  errorMessage,
  propertiesLocal,
  isShowOnlyIcon,
  productPersonalisedPrice = '0'
}: props) {
  if (!product || !variantSelectedId || !variantSelected) {
    return
  }
  const [currentItem, setCurrentItem] = useState<OmneoListItem | null>(null)
  const [currentList, setCurrentList] = useState<OmneoList | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [properties, setProperties] = useState<any>([])
  const [wishlistLists, setWishlistLists] = useState<OmneoList[]>([])
  const wishlistSlideoutEl = document.querySelector('.js-wishlist-slideout')

  const openSlideout = () => {
    if (GM_STATE.customer.logged) {
      if (disableWishlist === 'true') {
        document.dispatchEvent(new CustomEvent('add-wishlist-error'))
      } else {
        wishlistSlideoutEl?.setAttribute('product', JSON.stringify(product))
        wishlistSlideoutEl?.setAttribute('variant-selected', JSON.stringify(variantSelected))
        wishlistSlideoutEl?.setAttribute('product-bi-line', productBiLine)
        wishlistSlideoutEl?.setAttribute('variant-selected-id', variantSelectedId.toString())
        wishlistSlideoutEl?.setAttribute('template-suffix', templateSuffix)
        wishlistSlideoutEl?.setAttribute('product-personalised-price', productPersonalisedPrice)
        if (propertiesLocal) {
          wishlistSlideoutEl?.setAttribute('properties-local', JSON.stringify(propertiesLocal))
        }
        openModal('wishlist-slideout')
        document.body.classList.remove('no-scroll')
      }
    } else {
      let valueReturnTo = `/products/${product.handle}?variant=${variantSelectedId.toString()}`

      if (properties.length > 0) {
        const isPairKey = properties.some((property) => property.hasOwnProperty('pair'))

        if (isPairKey) {
          const isPair = properties.find((property) => property.hasOwnProperty('pair'))?.pair
          valueReturnTo = valueReturnTo + `&pair=${isPair.toString()}`
        }
      }

      sessionStorage.setItem('returnTo', valueReturnTo)
      openModal('account-slideout')
    }
  }

  const removePropertiesTime = (properties) => {
    return properties.map((property) => {
      property.properties = Object.keys(property.properties)
        .sort()
        .reduce(function (result, key) {
          result[key] = property.properties[key]
          return result
        }, {})
      if (property.hasOwnProperty('section-id')) {
        delete property['section-id']
      }
      if (property.properties.hasOwnProperty('_uniqueTimeStamp')) {
        property.properties._uniqueTimeStamp = 1
      }
      if (property.properties.hasOwnProperty('_uniqueItem')) {
        property.properties._uniqueItem = 1
      }
      if (property.properties.hasOwnProperty('pairId')) {
        property.properties.pairId = 1
      }
      if (property.hasOwnProperty('core_product_single_id') && property.hasOwnProperty('core_product_multi_id')) {
        delete property['option-0']
        delete property['option-1']
      }

      return Object.keys(property)
        .sort()
        .reduce(function (result, key) {
          result[key] = property[key]
          return result
        }, {})
    })
  }

  const checkVariantAdded = async () => {
    if (disableWishlist === 'true') {
      return
    }
    setIsLoading(true)
    try {
      setCurrentItem(null)
      setCurrentList(null)

      const propertiesWithoutTime = removePropertiesTime(properties)

      if (variantSelectedId && wishlistLists?.length > 0) {
        const itemList = wishlistLists.find((list: any) =>
          list.items.some(
            (item: any) =>
              item?.meta?.variantId.toString() === variantSelectedId.toString() &&
              JSON.stringify(removePropertiesTime(item?.meta?.properties)) === JSON.stringify(propertiesWithoutTime)
          )
        )

        if (itemList) {
          setCurrentList(itemList)
          const currentItem = itemList.items.find((item: any) => {
            const propertiesWithoutTimeMeta = removePropertiesTime(item?.meta?.properties)
            return (
              item?.meta?.variantId.toString() === variantSelectedId.toString() &&
              JSON.stringify(propertiesWithoutTimeMeta) === JSON.stringify(propertiesWithoutTime)
            )
          })
          currentItem && setCurrentItem(currentItem)
        }
      }
    } catch (error) {
      console.error('error', error)
    }
    setIsLoading(false)
  }

  const removeWishlistItem = () => {
    if (!currentList || !currentItem) {
      return
    }
    setIsLoading(true)
    const listHandle = currentList.handle
    const itemId = currentItem.id
    window.omneoService.removeItemFromList(listHandle, itemId).then(() => {
      setCurrentItem(null)
      setCurrentList(null)
      setIsLoading(false)
      globalEmitter.emit('triggerNotification', { message: 'Item removed successfully', type: 'success' })
      globalEmitter.emit(emitterProps.wishlist.remove, { listHandle, itemId })
    })
  }

  const handleButtonClick = () => {
    if (currentItem) {
      removeWishlistItem()
    } else {
      openSlideout()
    }
  }

  const triggerEvent = () => {
    globalEmitter.on(emitterProps.wishlist.add, checkVariantAdded)
  }

  useEffect(() => {
    globalEmitter.on('product:addToWishlist', (values) => {
      setProperties(values.addQueue)
    })

    const wishlistGlobal = sessionStorage.getItem('wishlistsGlobal')
    if (wishlistGlobal && JSON.parse(wishlistGlobal).length > 0) {
      setWishlistLists(JSON.parse(wishlistGlobal))
    }
  }, [])

  useEffect(() => {
    if (propertiesLocal) {
      setProperties(propertiesLocal)
    }
    checkVariantAdded().then()
  }, [disableWishlist, propertiesLocal])

  useEffect(() => {
    if (GM_STATE.customer.logged) {
      const iconHeart = document.querySelector('.js-icon-heart-product')
      if (iconHeart) {
        if (wishlistLists.length > 0) {
          iconHeart.classList.add('animation-pulse-zoom')
        }
        globalEmitter.on('fetchWishlist', (lists) => {
          if (lists?.length > 0) {
            setWishlistLists(lists)
          }
        })
        setTimeout(() => {
          iconHeart.classList.remove('animation-pulse-zoom')
          checkVariantAdded().then()
          triggerEvent()
        }, 1000)
      }
    }
  }, [variantSelectedId, wishlistLists, properties])

  return (
    <button
      className={`relative js-button-wishlist-product ${className} ${
        !isShowOnlyIcon ? 'Button Button--tertiary flex justify-center items-center w-full overflow-hidden group' : 'mt-0'
      }`}
      type="button"
      onClick={handleButtonClick}
    >
      {!isLoading ? (
        !isShowOnlyIcon ? (
          <span
            className={`relative flex justify-center items-center gap-3 w-full h-full ${
              disableWishlist && JSON.parse(disableWishlist) && !currentItem
                ? 'opacity-100 transition-all duration-200 ease-in-out group-hover:opacity-0 group-hover:-translate-y-[30px]'
                : ''
            }`}
          >
            {currentItem ? wishlistDashboardState.removeFromWishlist : wishlistDashboardState.addToWishlist}
            <HeartIcon className={`js-icon-heart-product w-3 h-3 ${currentItem ? 'fill-white group-hover:fill-ink' : ''}`} />
          </span>
        ) : (
          <HeartIcon className={`js-icon-heart-product ${currentItem ? 'fill-ink' : 'text-subdued'}`} />
        )
      ) : (
        <LoaderSpin />
      )}
      {disableWishlist && JSON.parse(disableWishlist) && errorMessage && !currentItem && !isShowOnlyIcon && (
        <span className="absolute bottom-[-15px] left-1/2 -translate-x-1/2 -translate-y-1/2 h-fit opacity-0 transition-all duration-200 ease-in-out group-hover:bottom-[5px] group-hover:opacity-100">
          {errorMessage}
        </span>
      )}
    </button>
  )
}
